/**
 * מטמון לנתוני בסיס של עמוד הדוחות (חנויות, מכשירים, תיקונים, עובדים, חלקים).
 *
 * - שכבה 1: זיכרון באפליקציה — חוזרים לעמוד הדוחות בלי לקרוא שוב ל-Firestore עד שפג תוקף (TTL).
 * - שכבה 2: Firestore עם persistentLocalCache (firebaseConfig) — getDocs עדיין יכול לשרת מ-IndexedDB אחרי ביקור קודם.
 * - "רענן מהשרת": getDocsFromServer — דילוג על המטמון המקומי של ה-SDK ושליפה מלאה מהרשת.
 *
 * זה לא מחליף אגרגציות בשרת לדוחות כבדים; זה מפחית קריאות חוזרות בזמן עבודה עם אותו סט נתונים.
 */
import {
  collection,
  getDocs,
  type QuerySnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import type { Repair } from '../types/typesRepair';
import type { Store } from '../types/typesStores';
import type { Device } from '../types/typesDevices';
import type { Employee } from '../types/typesEmployees';
import type { SparePart } from '../types/typesSpareParts';
import {
  fetchDevicesFromHybrid,
  fetchRepairsFromHybrid,
  fetchStoresFromHybrid,
} from './entitiesHybridService';
import { fetchAllSparePartsFromHybrid } from './sparePartsHybridService';

export const REPORTS_BASE_DATA_TTL_MS = 5 * 60 * 1000;

export type ReportsBaseCollections = {
  stores: Store[];
  devices: Device[];
  repairs: Repair[];
  employees: Employee[];
  spareParts: SparePart[];
};

export type ReportsBaseFetchSource = 'memory' | 'firestore';

export type ReportsBaseFetchResult = {
  data: ReportsBaseCollections;
  source: ReportsBaseFetchSource;
};

type CacheEntry = {
  userKey: string;
  expiresAt: number;
  data: ReportsBaseCollections;
};

let memoryEntry: CacheEntry | null = null;
let memoryCatalogEntry: CacheEntry | null = null;

export function invalidateReportsBaseDataCache(): void {
  memoryEntry = null;
  memoryCatalogEntry = null;
}

function mapEmployees(snap: QuerySnapshot<DocumentData>): Employee[] {
  return snap.docs
    .filter((d) => {
      const data = d.data();
      return data.role !== 'external_viewer';
    })
    .map((d) => {
      const data = d.data();
      return {
        id: d.id,
        name: data.fullName || data.name || '',
        role: data.role || '',
        phone: data.phone || '',
        email: data.email || '',
        active: data.active !== false,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } as Employee;
    });
}

/**
 * @param userKey מפתח לפי משתמש (לרוב uid) כדי לא לערבב מטמון בין משתמשים
 */
export async function fetchReportsBaseCollections(
  userKey: string,
  options?: { forceServer?: boolean; ttlMs?: number },
): Promise<ReportsBaseFetchResult> {
  const ttl = options?.ttlMs ?? REPORTS_BASE_DATA_TTL_MS;
  const now = Date.now();

  if (!options?.forceServer && memoryEntry && memoryEntry.userKey === userKey && memoryEntry.expiresAt > now) {
    return { data: memoryEntry.data, source: 'memory' };
  }

  const [stores, devices, spareParts, employeesSnap] = await Promise.all([
    fetchStoresFromHybrid(),
    fetchDevicesFromHybrid(),
    fetchAllSparePartsFromHybrid(),
    getDocs(collection(db, 'users')),
  ]);
  const repairs: Repair[] = [];
  let offset = 0;
  const pageSize = 200;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const page = await fetchRepairsFromHybrid({ limit: pageSize, offset });
    const rows = Array.isArray(page.data) ? page.data : [];
    repairs.push(...rows);
    if (rows.length < pageSize) break;
    offset += rows.length;
  }
  const data: ReportsBaseCollections = {
    stores,
    devices,
    repairs,
    employees: mapEmployees(employeesSnap as QuerySnapshot<DocumentData>),
    spareParts,
  };

  memoryEntry = {
    userKey,
    expiresAt: now + ttl,
    data,
  };

  return { data, source: 'firestore' };
}

/**
 * טעינת קטלוג לדוחות בלי אוסף repairs — כשהאגרגציה בשרת פעילה.
 */
export async function fetchReportsCatalogOnly(
  userKey: string,
  options?: { forceServer?: boolean; ttlMs?: number },
): Promise<ReportsBaseFetchResult> {
  const ttl = options?.ttlMs ?? REPORTS_BASE_DATA_TTL_MS;
  const now = Date.now();

  if (
    !options?.forceServer &&
    memoryCatalogEntry &&
    memoryCatalogEntry.userKey === userKey &&
    memoryCatalogEntry.expiresAt > now
  ) {
    return { data: memoryCatalogEntry.data, source: 'memory' };
  }

  const [stores, devices, spareParts, employeesSnap] = await Promise.all([
    fetchStoresFromHybrid(),
    fetchDevicesFromHybrid(),
    fetchAllSparePartsFromHybrid(),
    getDocs(collection(db, 'users')),
  ]);
  const data: ReportsBaseCollections = {
    stores,
    devices,
    repairs: [],
    employees: mapEmployees(employeesSnap as QuerySnapshot<DocumentData>),
    spareParts,
  };

  memoryCatalogEntry = {
    userKey,
    expiresAt: now + ttl,
    data,
  };

  return { data, source: 'firestore' };
}
